import { useEffect, useRef } from 'react';

import { DXF_CO_APPLICABLE_ROLES, type DxfMappingRole } from '@frontend/api';
import { Box, CheckmarkIcon, Flex, Text } from '@frontend/legacy-components';

import { useDxfViewerSession } from '../DxfViewerContext';
import { DXF_Z_INDEX } from '../constants';
import { MAPPING_ROLE_DEFINITIONS } from '../session/roles';
import { useReadableStore } from '../session/useSessionState';

/**
 * The right-click role menu: the same four roles as the flyout, with a color
 * swatch and a checkmark on every role the selection already shares.
 *
 * Visibility is `session.roleChrome.kind === 'context'` — exclusive with the
 * flyout, and hidden when the selection is empty, without this component
 * naming either. Positioned once at the click rather than tracked afterward.
 *
 * Most closing rules live in `inputController` (any new canvas gesture, Escape)
 * and in `toolController`/`modeController` (switching tools or leaving mapping
 * mode); the one this component owns itself is a press anywhere outside the
 * menu.
 */
export const DxfMappingContextMenu = () => {
  const session = useDxfViewerSession();
  const chrome = useReadableStore(session.roleChrome);
  const sharedRoles = useReadableStore(session.mapping.selectionSharedRoles);
  const menuRef = useRef<HTMLDivElement>(null);

  const isOpen = chrome.kind === 'context';
  const anchor = chrome.kind === 'context' ? chrome.anchor : null;

  useEffect(() => {
    const handlePointerUp = () => {
      session.roleChrome.release();
    };
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);
    return () => {
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [session]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handlePointerDownOutside = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        session.roleChrome.dismiss();
      }
    };
    // Capture phase, so an outside press closes the menu even where its own
    // handler would otherwise stop the event from bubbling back up here.
    document.addEventListener('pointerdown', handlePointerDownOutside, true);
    return () =>
      document.removeEventListener(
        'pointerdown',
        handlePointerDownOutside,
        true,
      );
  }, [isOpen, session]);

  if (!isOpen || !anchor) {
    return null;
  }

  const handlePick = (role: DxfMappingRole) => {
    if (DXF_CO_APPLICABLE_ROLES.has(role)) {
      session.mapping.toggleRoleOnSelection(role);
      return;
    }
    session.mapping.toggleRoleOnSelectionAndClear(role);
    session.roleChrome.close();
  };

  return (
    <Box
      ref={menuRef}
      position="absolute"
      top={0}
      left={0}
      transform={`translate(${anchor[0]}px, ${anchor[1]}px)`}
      zIndex={DXF_Z_INDEX.FLOATING}
      bg="page.surface"
      border="1px solid"
      borderColor="separator.border"
      borderRadius="lg"
      boxShadow="popover"
      py={0.5}
      minW="9rem"
      pointerEvents="auto"
      userSelect="none"
      data-testid="dxf-mapping-context-menu"
      role="menu"
      aria-label="Assign role to selection"
    >
      {MAPPING_ROLE_DEFINITIONS.map(({ role, label, color }) => {
        const isApplied = sharedRoles.has(role);

        return (
          <Flex
            key={role}
            as="button"
            type="button"
            role="menuitemcheckbox"
            aria-label={`Toggle ${label} on selection`}
            aria-checked={isApplied}
            align="center"
            width="100%"
            px={2}
            py={1}
            gap={1.5}
            cursor="pointer"
            _hover={{ bg: 'interaction.surfaceHover' }}
            onClick={() => handlePick(role)}
          >
            <Box flexShrink={0} boxSize={1.5} borderRadius="full" bg={color} />
            <Text
              flex={1}
              minW={0}
              textAlign="left"
              fontSize="sm"
              fontWeight="medium"
            >
              {label}
            </Text>
            {isApplied && (
              <CheckmarkIcon
                boxSize={3}
                color="interaction.main"
                flexShrink={0}
                aria-hidden
              />
            )}
          </Flex>
        );
      })}
    </Box>
  );
};
DxfMappingContextMenu.displayName = 'DxfMappingContextMenu';
